import styled from '@emotion/styled';
import '@material/web/icon/icon.js';
import { ReactElement } from 'react';
import { Monogram } from '../atoms/monogram/monogram';
import { Icon } from '../atoms/icon/icon';
import { StyledDiv } from '../common/components/styled-div';
import { Switch } from '../atoms/switch/switch';
import { Checkbox } from '../atoms/checkbox/checkbox';
import { Radio } from '../atoms/radio/radio';
import { Badge } from '../atoms/badge/badge';

// Shared styled components
export const ChipFilterContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-md);
  overflow-y: auto;
  white-space: nowrap;
  height: 40px;
  margin-top: var(--rk-spacing-lg);
`;

// Common interfaces
export interface PrefixSuffixContentProps {
  direction?: 'leading' | 'trailing';
  type:
    | 'monogram'
    | 'icon'
    | 'image'
    | 'blank-image'
    | 'switch'
    | 'checkbox'
    | 'radio'
    | 'badge';
  source?: string;
  name?: string | ReactElement;
  isUpperCase?: boolean;
  monogramSize?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  cursor?: string;
  badgeText?: string;
  badgeColor?: string;
  badgeType?: 'filled' | 'outlined';
  badgeSize?: 'small' | 'large';
  checked?: boolean;
  color?: string;
}

// Shared components
export const PrefixSuffixContent = ({
  direction = 'leading',
  source,
  type,
  name,
  isUpperCase = true,
  monogramSize,
  onClick,
  cursor,
  badgeText,
  badgeColor,
  badgeType,
  badgeSize,
  checked = false,
  color = '--md-sys-color-primary',
}: PrefixSuffixContentProps) => {
  const renderContent = () => {
    switch (type) {
      case 'monogram':
        return (
          <Monogram
            name={name || ''}
            isUpperCase={isUpperCase}
            size={monogramSize}
          />
        );
      case 'icon':
        return (
          <Icon
            isMenuAnchor={true}
            name={source ?? ''}
            slot={direction === 'leading' ? 'start' : 'end'}
            onClick={onClick}
            cursor={cursor}
            color={color}
          />
        );
      case 'image':
        return (
          <img
            slot={direction === 'leading' ? 'start' : 'end'}
            height={72}
            width={58}
            src={source}
            alt="dynamic content"
          />
        );
      case 'blank-image':
        return (
          <StyledDiv
            slot={direction === 'leading' ? 'start' : 'end'}
            height="72px"
            width="58px"
            background="--md-sys-color-on-surface-variant"
            justifyContent="center"
            alignItem="center"
          >
            <Icon name="remove_red_eye" color="--md-sys-color-on-primary" />
          </StyledDiv>
        );
      case 'badge':
        return (
          <Badge
            label={badgeText || ''}
            size={badgeSize || 'large'}
            color={badgeColor}
            type={badgeType}
          />
        );
      case 'switch':
        return <Switch onChange={() => {}} />;
      case 'checkbox':
        return <Checkbox onChange={() => {}} />;
      case 'radio':
        return <Radio option={{ content: '', value: '', checked: checked }} />;
      default:
        return null;
    }
  };

  return renderContent();
};
